import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";
import { resolveMedia } from "@/lib/api";

export default function About() {
  const { content } = useContent();
  const about = content.about || {};
  const site = content.site || {};
  const portrait = content.assets?.authorPortrait;
  const r1 = useReveal();
  const r2 = useReveal();

  // Render headline with optional italic wrap on a parenthesised span.
  // We treat the headline as plain text but underline the last two words italically.
  const renderHeadline = () => {
    const text = about.headline || "Nuo meno iki tikrų erdvių.";
    const parts = text.split(" ");
    if (parts.length < 3) return text;
    const tail = parts.slice(-2).join(" ");
    const head = parts.slice(0, -2).join(" ");
    return (
      <>
        {head} <span className="italic">{tail}</span>
      </>
    );
  };

  return (
    <section data-testid="about-section" className="bg-[#f9f8f6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-[140px] md:pt-[180px] pb-24 md:pb-40 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-7 reveal" ref={r1}>
          <div className="flex items-center gap-4 mb-10">
            <span className="overline">— 01 / Apie mane</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1a1a1a] max-w-[16ch]">
            {renderHeadline()}
          </h1>
          <div className="mt-10 space-y-6 text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-xl">
            {(about.paragraphs || []).map((p, i, arr) => (
              <p key={i} className={i === arr.length - 1 ? "text-[#1a1a1a]" : ""}>
                {p}
              </p>
            ))}
          </div>

          {Array.isArray(about.stats) && about.stats.length > 0 && (
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {about.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-serif-display text-3xl md:text-4xl text-[#1a1a1a]">
                    {s.n}
                  </div>
                  <div className="overline mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-14 flex flex-wrap gap-4">
            <Link to="/projektai" className="btn-primary">
              Peržiūrėti projektus <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/kontaktai" className="btn-secondary">
              Susisiekti
            </Link>
          </div>
        </div>

        <div className="md:col-span-5 reveal md:pt-24" ref={r2}>
          <div className="relative">
            <div className="aspect-[3/4] w-full bg-[#efedea] overflow-hidden">
              {portrait ? (
                <img
                  src={resolveMedia(portrait)}
                  alt="Autorius"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-[#9a9690] relative">
                  <div className="w-32 h-32 rounded-full bg-[#e5e2dd] mb-6" />
                  <div className="overline">Nuotrauka — netrukus</div>
                  <div className="absolute inset-0 pointer-events-none border border-[#e5e2dd]" />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="overline">Autorius</span>
              <span className="text-xs text-[#5c5c5c] font-light tracking-wide">
                {site.city || "Klaipėda"}, {site.country || "Lietuva"}
              </span>
            </div>
          </div>

          {about.blockquote && (
            <blockquote className="mt-12 border-l border-[#a66e4b] pl-6 italic text-[#3a3a3a] text-base leading-relaxed font-light">
              „{about.blockquote}“
            </blockquote>
          )}
        </div>
      </div>
    </section>
  );
}
